import { useState } from 'react';
import { ArrowDown, ArrowUp, Loader2 } from 'lucide-react';
import type { DashboardWidgetKey } from '@efacture/core/sector';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from '@/lib/i18n';

export function useWidgetLabel() {
  const { t } = useTranslation();
  return (key: DashboardWidgetKey): string => {
    const label = t(`dashboard.widget.${key}` as Parameters<typeof t>[0]);
    return label.startsWith('dashboard.widget.') ? key : label;
  };
}

/** `selected` first, in its order, then the other `options` unticked. */
function initialRows<K extends string>(options: readonly K[], selected: readonly K[]) {
  const chosen = selected.filter((key) => options.includes(key));
  return [
    ...chosen.map((key) => ({ key, shown: true })),
    ...options.filter((key) => !chosen.includes(key)).map((key) => ({ key, shown: false })),
  ];
}

export interface DashboardWidgetPickerProps<K extends string = DashboardWidgetKey> {
  /** Everything that may be chosen. */
  options: readonly K[];
  /** The ticked widgets, in order. */
  selected: readonly K[];
  pending: boolean;
  onSave: (keys: K[]) => void;
  /** The French label of a widget; `/dashboard`'s labels when absent. */
  labelOf?: (key: K) => string;
  saveLabel: string;
  /** A second action next to Save (reset, cancel), when the caller has one. */
  secondary?: { label: string; onClick: () => void; disabled?: boolean }[];
}

/**
 * Show or hide each widget and move it up or down. Keyboard and screen
 * reader friendly: every action is a labelled button or checkbox.
 */
export function DashboardWidgetPicker<K extends string = DashboardWidgetKey>({
  options,
  selected,
  pending,
  onSave,
  labelOf: labelOverride,
  saveLabel,
  secondary = [],
}: DashboardWidgetPickerProps<K>) {
  const { t } = useTranslation();
  const dashboardLabel = useWidgetLabel();
  const labelOf = labelOverride ?? ((key: K) => dashboardLabel(key as unknown as DashboardWidgetKey));
  const [rows, setRows] = useState(() => initialRows(options, selected));

  const move = (index: number, delta: -1 | 1) =>
    setRows((current) => {
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  const toggle = (index: number, shown: boolean) =>
    setRows((current) => current.map((row, i) => (i === index ? { ...row, shown } : row)));

  const chosen = rows.filter((row) => row.shown).map((row) => row.key);

  return (
    <section
      aria-label={t('dashboard.customize.title')}
      className="rounded-xl border border-slate-200 bg-white p-4 space-y-4"
    >
      <p className="text-sm text-slate-600">{t('dashboard.customize.help')}</p>
      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {rows.map((row, index) => {
          const label = labelOf(row.key);
          const id = `dashboard-widget-${row.key}`;
          return (
            <li key={row.key} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2">
              <Checkbox
                id={id}
                checked={row.shown}
                onCheckedChange={(checked) => toggle(index, checked === true)}
              />
              <label htmlFor={id} className="flex-1 text-sm font-medium text-slate-900">
                {label}
              </label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={index === 0}
                aria-label={t('dashboard.customize.moveUp').replace('{widget}', label)}
                onClick={() => move(index, -1)}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={index === rows.length - 1}
                aria-label={t('dashboard.customize.moveDown').replace('{widget}', label)}
                onClick={() => move(index, 1)}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
            </li>
          );
        })}
      </ul>
      {chosen.length === 0 && (
        <p role="alert" className="text-sm text-amber-700">
          {t('dashboard.customize.atLeastOne')}
        </p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {secondary.map((action) => (
          <Button
            key={action.label}
            type="button"
            variant="outline"
            disabled={pending || action.disabled}
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ))}
        <Button type="button" disabled={pending || chosen.length === 0} onClick={() => onSave(chosen)}>
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          {saveLabel}
        </Button>
      </div>
    </section>
  );
}
